import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";

// =======================================================
//   Array with All Types
// =======================================================
// bg: keys are the fields required for the user to see
// STEP ONE
//   - if the key contains an object, e.g. dimension.length, ["key", ["child_key_1", "child_key_2", ...]]
//   - if the key contains an array, e.g. additionalCharges[], ["key", []] --> ([x][1] is not used)
// STEP TWO
//   - add the key under const response { ... switch(type) key: things_to_do }
// STEP THREE
//   - if it is an object, things_to_do = setUpDetails, else things_to_do = setUpArrayDetails. This is done in L331* (switch)
// STEP FOUR
//   - a default format has been given, but if there needs to be customisation, add the key to the switch case, and create a function for it
const allTypes = [
  ["dimension", ["length", "height", "width"]],
  ["senderInfo", ["fullName", "contactEmail", "contactNumber", "address"]],
  ["receiverInfo", ["fullName", "contactEmail", "contactNumber", "address"]],
  ["additionalCharges", []],
  ["remarks", []],
  ["weight", []],
  ["hasInsurance", []],
  ["parcels", []],
  ["specialCondition", []],
  ["specialCare", []],
  ["notes", []],
  ["orderStatusInfo", ["orderStatus"]],
];

// =======================================================
//   Uppercase Formatter
// =======================================================
const formatUpperCase = (text: string, toSeparate = false) => {
  // capitalise the first character
  // if toSeparate is true, put a space before every capital letter except the first, otherwise just combine as normal
  const secondText = toSeparate
    ? text
        ?.substring(1)
        ?.replace(/([A-Z])/g, " $1")
        .trim()
    : text?.substring(1);

  return text?.charAt(0).toUpperCase() + secondText;
};

// =======================================================
//   Lowercase Formatter
// =======================================================
const formatLowerCase = (text: any, toSeparate = false, firstLower = false) => {
  // turn the first character lowercase or remain
  const firstText = firstLower
    ? text?.charAt(0).toLowerCase()
    : text?.charAt(0);

  // if toSeparate is true, remove underscore and put a space before every capital letter except the first, otherwise just combine as normal
  const secondText = toSeparate
    ? text?.substring(1)?.toLowerCase()?.replaceAll("_", " ")
    : text?.substring(1)?.toLowerCase();

  return firstText + secondText;
};

// =======================================================
//   Set Up: Array
// =======================================================
// ---- if oldref / newref is an array, use this
// oldref: the old array that has the information needed
// newref: the new array that has the information needed
// types: the possible fields that parent may have
const setUpArrayDetails = (oldref: any, newref: any, type) => {
  let detailArray: any = [];

  // for additional charges
  const additonalChargesFormatter = (oldItem, newItem) => {
    let text = "";

    // check and format
    // -- if name is present, check if type is OTHERS, and format it as title: Changed from old amount to new amount
    // -- -- else format it as title: Changed from old name to new name
    // -- else if type is different, format it as Changed from old type to new type
    if (oldItem?.name || newItem?.name) {
      if (oldItem?.type == "OTHERS" || newItem?.type == "OTHERS") {
        let title = oldItem?.name ?? newItem?.name;

        if (!oldItem?.amount) {
          text = `Added ${title}`;
        } else if (!newItem?.amount) {
          text = `Removed ${title}`;
        } else {
          text = `${title}: Changed from ${
            oldItem?.amount
              ? oldItem?.amount < 0
                ? `-RM ${oldItem?.amount.replace("-", "").trim()}`
                : `RM ${oldItem?.amount.replace("-", "").trim()}`
              : "RM 0"
          } to ${
            newItem?.amount
              ? newItem?.amount < 0
                ? `-RM ${newItem?.amount.replace("-", "").trim()}`
                : `RM ${newItem?.amount.replace("-", "").trim()}`
              : "RM 0"
          }`;
        }
      } else {
        let title = oldItem?.type ?? newItem?.type;
        text = `${formatLowerCase(title, true)}: Changed from ${
          oldItem?.name ?? "None"
        } to ${newItem?.name ?? "None"}`;
      }
    } else if (oldItem?.type != newItem?.type) {
      let oldFormatted = oldItem?.type
        ? oldItem?.type?.charAt(0) +
          oldItem?.type?.substring(1).toLowerCase().replaceAll("_", " ")
        : "None";
      const newFormatted = newItem?.type
        ? newItem?.type?.charAt(0) +
          newItem?.type?.substring(1).toLowerCase().replaceAll("_", " ")
        : "None";

      text = `Changed from ${oldFormatted} to ${newFormatted}`;
    }

    return text;
  };

  // for notes
  const notesRendererFormatter = (oldItem, newItem) => {
    let text = "";

    // if old is empty, but new has something: this is add
    if (oldItem == null && newItem) {
      text = `Added new note with content: ${newItem?.note}`;
    } else if (oldItem && newItem == null) {
      text = `Removed note: ${oldItem?.note}`;
    } else if (oldItem && newItem) {
      // possible to have many changes at once, ignore null values
      text = `Changes made: `;
      let addText = "";

      if (oldItem?.noteType != newItem?.noteType) {
        // if type is different
        addText =
          addText + `type from ${oldItem?.noteType} to ${newItem?.noteType}, `;
      }
      if (oldItem?.note != newItem?.note) {
        // if note is different
        addText =
          addText + `content from ${oldItem?.note} to ${newItem?.note}, `;
      }
      if (oldItem?.noteStatus != newItem?.noteStatus) {
        // if status has been changed
        addText =
          addText +
          `status from ${oldItem?.noteStatus} to ${newItem?.noteStatus}, `;
      }

      // if addText is 0, some other field has changed and is irrelevant to the user
      if (addText?.length > 0) {
        // remove the comma and additional space
        text = text + addText.slice(0, -2);
      }
    }

    return text;
  };

  const textFormattingByArray = (oldItems, newItems) => {
    let text = "";

    switch (type) {
      case "additionalCharges":
        // if there is specialCondition, show another description
        const typeCheck = oldItems?.type ?? newItems?.type;

        if (typeCheck == "SPECIAL_CONDITION")
          text = `Affected by ${formatLowerCase(typeCheck, true, true)}`;
        else text = additonalChargesFormatter(oldItems, newItems);
        break;
      case "parcels":
        const label = "Type of goods";
        let addText = "";

        //  if the names dont match, the type of goods has changed
        if (oldItems?.name != newItems?.name) {
          addText = `Changed from ${oldItems?.name ?? "None"} to ${
            newItems?.name ?? "None"
          }, `;
        }

        // if the values dont match, the values have changed
        if (oldItems?.goodsValue != newItems?.goodsValue) {
          addText =
            addText +
            " " +
            `Value of goods have changed from ${
              oldItems?.goodsValue ?? "0"
            } to ${newItems?.goodsValue ?? "0"}  `;
        }

        text = `${label}: ${addText.slice(0, -2).trim()}`;
        break;
      case "notes":
        text = notesRendererFormatter(oldItems, newItems);
        break;
      default:
    }

    return text;
  };

  // the reference passed is an array, but the elements are related
  // if changes.length > history.length, something is added, otherwise removed
  // it is possible that old can have less data than new, but we still want to show the changes (and vice versa)
  const refLength =
    oldref?.length > newref?.length ? oldref?.length : newref?.length;

  for (let i = 0; i < refLength; i++) {
    // possible that there is undefined values, e.g. newly added notes
    const oldLoopItem = oldref == undefined ? null : oldref[i];
    const newLoopItem = newref == undefined ? null : newref[i];

    detailArray.push(textFormattingByArray(oldLoopItem, newLoopItem));
  }

  return detailArray;
};

// =======================================================
//   Set Up: Object
// =======================================================
// ---- if oldref / newref is an object, use this
// oldref: the old array that has the information needed
// newref: the new array that has the information needed
// types: the possible fields that parent may have
// unit: the unit for the value (kg, cm, etc)
const setUpDetails = (
  oldref: any,
  newref: any,
  types: string[],
  unit: string,
  payload?: any,
) => {
  let detailArray: any = [];

  const textFormatting = (field) => {
    let text = "";
    let title = "";

    if (typeof field == "string") {
      title = formatUpperCase(field);
    }

    const extractCoordinate = (coordinate: { lat?: string; lon?: string }) => {
      const currentCoordinate = payload.order.receiverInfo.address.coordinate;
      return (
        coordinate.lat ||
        currentCoordinate.lat + ", " + coordinate.lon ||
        currentCoordinate.lon
      );
    };

    // formatting
    switch (field) {
      case "contactNumber":
        text = `${title}: Changed from ${oldref?.[field]?.fullNumber} to ${newref?.[field]?.fullNumber}`;
        break;
      case "address":
        text = `${title}: Changed from ${
          oldref?.[field]?.street1 ? oldref?.[field]?.street1 + ", " : ""
        }${oldref?.[field]?.street2 ? oldref?.[field]?.street2 + ", " : ""}${
          oldref?.[field]?.city ? oldref?.[field]?.city + ", " : ""
        }${oldref?.[field]?.postcode ? oldref?.[field]?.postcode + ", " : ""}${
          oldref?.[field]?.state ? oldref?.[field]?.state + "," : ""
        }${
          oldref?.[field]?.coordinate
            ? extractCoordinate(oldref?.[field]?.coordinate) + ", "
            : ""
        }
        to ${newref?.[field]?.street1 ? newref?.[field]?.street1 + ", " : ""}${
          newref?.[field]?.street2 ? newref?.[field]?.street2 + ", " : ""
        }${newref?.[field]?.city ? newref?.[field]?.city + ", " : ""}${
          newref?.[field]?.postcode ? newref?.[field]?.postcode + ", " : ""
        }${newref?.[field]?.state ? newref?.[field]?.state + "," : ""}
        ${
          newref?.[field]?.coordinate
            ? extractCoordinate(newref?.[field]?.coordinate) + ", "
            : ""
        }`;
        break;
      default:
        // unit is optional
        text = `${title}: Changed from ${toCapitalisedSpaced(oldref?.[field])}${
          unit ? ` ${unit}` : ""
        } to ${toCapitalisedSpaced(newref?.[field])}${unit ? ` ${unit}` : ""}`;
    }

    return text;
  };

  for (let i = 0; i < types.length; i++) {
    if (oldref?.[types[i]] || newref?.[types[i]]) {
      const field = types[i];
      detailArray.push(textFormatting(field));
    }
  }

  return detailArray;
};

// =======================================================
//   Model
// =======================================================
const response = (data: any) => {
  // get the necessary data first
  const info = data?.orderVersionHistory?.map((order) => ({
    updatedAt: order?.createdAt,
    updatedBy: order?.profileData?.fullName,
    history: {
      ...order?.oldDocDifference,
    },
    changes: {
      ...order?.newDocDifference,
    },
    payload: order.payload,
  }));

  let dataArray: any = [];

  for (let i = 0; i < info?.length; i++) {
    let typeObject;

    // run through all possible types and return a matching type
    for (let j = 0; j < allTypes.length; j++) {
      const index: any = allTypes[j][0];

      // it is possible that new changes may contain more objects than the old
      // always use the one with more keys/properties for comparison
      // --- if new.length == old.length, check if the old.index exists
      // --- if old.index doesnt exist, it is possible that new.index does exist
      let compareRef =
        Object.keys(info[i]?.history).length >
        Object.keys(info[i]?.changes).length
          ? info[i]?.history?.[index]
            ? info[i]?.history
            : info[i]?.changes
          : info[i]?.changes;

      // keys with boolean type can fail this check if only checking for contents
      if (typeof compareRef?.[index] == "boolean" || compareRef?.[index]) {
        typeObject = { type: index, fields: allTypes[j][1] };

        let details: any = [];
        let unit = "";
        let typeNameChange: any = null;

        // show units
        switch (typeObject.type) {
          case "weight":
            unit = "kg";
            break;
          case "dimension":
            unit = "cm";
            break;
          default:
        }

        // comparison: for case set 1: object, case set 2: array
        switch (typeObject.type) {
          case "dimension":
          case "senderInfo":
          case "receiverInfo":
            details = setUpDetails(
              info[i]?.history?.[typeObject.type],
              info[i]?.changes?.[typeObject.type],
              typeObject.fields,
              unit,
              info[i].payload,
            );
            break;
          case "orderStatusInfo":
            details = setUpDetails(
              info[i]?.history?.[typeObject.type],
              info[i]?.changes?.[typeObject.type],
              typeObject.fields,
              unit,
            );
            typeNameChange = "Status";
            break;
          case "additionalCharges":
          case "parcels":
          case "notes":
            details = setUpArrayDetails(
              info[i]?.history?.[typeObject.type],
              info[i]?.changes?.[typeObject.type],
              typeObject.type,
            );
            break;
          case "specialCondition":
            details.push(
              `Changed from ${
                info[i]?.history?.specialConditionData?.name ?? "None"
              } to ${info[i]?.changes?.specialConditionData?.name ?? "None"}`,
            );
            break;
          case "specialCare":
            details.push(
              `Changed from ${
                info[i]?.history?.specialCareData?.name ?? "None"
              } to ${info[i]?.changes?.specialCareData?.name ?? "None"}`,
            );
            break;
          case "hasInsurance":
            details.push(
              `Changed from ${
                info[i]?.history?.[typeObject.type] ? "Present" : "None"
              } to ${info[i]?.changes?.[typeObject.type] ? "Present" : "None"}`,
            );
            typeNameChange = "Insurance";
            break;
          default:
            details.push(
              `Changed from ${
                info[i]?.history?.[typeObject.type]
                  ? `${info[i]?.history?.[typeObject.type]} ${unit}`
                  : "None"
              } to ${
                info[i]?.changes?.[typeObject.type]
                  ? `${info[i]?.changes?.[typeObject.type]} ${unit}`
                  : "None"
              }`,
            );
        }

        dataArray.push({
          date: info[i]?.updatedAt,
          type: typeNameChange ?? formatUpperCase(typeObject?.type, true),
          updatedBy: info[i]?.updatedBy,
          details,
        });
      }
    }
  }

  return dataArray;
};

export default response;
